import { useCallback, useMemo, useState, type FC } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

import { Button, DataTable } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useComplianceResultsList } from "../../../queries/regulator-portal/compliance/useComplianceResultsList";
import { useStartComplianceVerification } from "../../../queries/regulator-portal/compliance/useStartComplianceVerification";
import { formatUtcTimestampWithLocal } from "../../../queries/regulator-portal/compliance/formatUtcTimestamp";
import type { ComplianceResult } from "../../../queries/regulator-portal/compliance/types";
import { buildRegulatorPortalResultPath } from "../regulatorPortalRoutes";
import ComplianceStatusBadge from "./ComplianceStatusBadge";
import { portalComplianceErrorMessage } from "./portalComplianceError";

import styles from "./Compliance.module.css";

const emptyValue = (value: string) => value || AppStrings.EMPTY_REASON;

const RegulatorPortalResults: FC = () => {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState<string | undefined>();
  const resultsQuery = useComplianceResultsList(cursor);
  const startVerification = useStartComplianceVerification();
  const rows = resultsQuery.data?.rows ?? [];
  const pagination = resultsQuery.data?.pagination;
  const canGoPrevious = Boolean(pagination?.previousCursor || cursor);
  const canGoNext = Boolean(pagination?.nextCursor);
  const showCursorNav = canGoPrevious || canGoNext;
  const loadError =
    resultsQuery.isError && rows.length === 0
      ? portalComplianceErrorMessage(
          resultsQuery.error,
          AppStrings.PAM_REGULATOR_PORTAL_RESULTS_LOAD_ERROR,
        )
      : null;

  const columns = useMemo(
    () => [
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_CLIENT,
        dataIndex: "client",
        key: "client",
        render: (value: string, record: ComplianceResult) => (
          <button
            type="button"
            className={styles.rowLink}
            onClick={() => navigate(buildRegulatorPortalResultPath(record.id))}
          >
            {emptyValue(value)}
          </button>
        ),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REGULATOR_NAME,
        dataIndex: "regulator",
        key: "regulator",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.STATUS,
        dataIndex: "status_summary",
        key: "status_summary",
        render: (value: string) =>
          value ? <ComplianceStatusBadge value={value} /> : AppStrings.EMPTY_REASON,
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_TRIGGER,
        dataIndex: "trigger",
        key: "trigger",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_ARCHIVED,
        dataIndex: "archived_at",
        key: "archived_at",
        render: (value: string) =>
          formatUtcTimestampWithLocal(value) || AppStrings.EMPTY_REASON,
      },
    ],
    [navigate],
  );

  const handleStartVerification = useCallback(async () => {
    try {
      const job = await startVerification.mutateAsync();
      message.success(AppStrings.PAM_REGULATOR_PORTAL_VERIFY_SUCCESS);
      if (job?.id) {
        navigate(buildRegulatorPortalResultPath(job.id));
      }
    } catch (error) {
      message.error(
        portalComplianceErrorMessage(
          error,
          AppStrings.PAM_REGULATOR_PORTAL_VERIFY_ERROR,
        ),
      );
    }
  }, [navigate, startVerification]);

  return (
    <section className={styles.page} data-testid="regulator-portal-results">
      <div className={styles.header}>
        <h1 className={styles.title}>{AppStrings.PAM_REGULATOR_PORTAL_RESULTS}</h1>
        <Button
          onClick={handleStartVerification}
          loading={startVerification.isPending}
          data-testid="regulator-portal-start-verification"
        >
          {AppStrings.PAM_REGULATOR_PORTAL_START_VERIFICATION}
        </Button>
      </div>
      {loadError ? (
        <p className={styles.message} role="alert">
          {loadError}
        </p>
      ) : null}
      {resultsQuery.isLoading ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_LOADING}</p>
      ) : null}
      {!resultsQuery.isLoading && !loadError && rows.length === 0 ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_RESULTS_EMPTY}</p>
      ) : null}
      {!resultsQuery.isLoading && rows.length > 0 ? (
        <div className={styles.tableSection}>
          <DataTable<ComplianceResult>
            rowKey="id"
            columns={columns}
            dataSource={rows}
            loading={resultsQuery.isFetching && rows.length === 0}
            pagination={false}
            scrollPreset="wide"
            data-testid="regulator-portal-results-table"
          />
        </div>
      ) : null}
      {!resultsQuery.isLoading && showCursorNav ? (
        <div className={styles.cursorRow} data-testid="regulator-portal-results-cursor">
          <Button
            intent="neutral"
            disabled={!canGoPrevious || resultsQuery.isFetching}
            onClick={() => setCursor(pagination?.previousCursor ?? undefined)}
            data-testid="regulator-portal-results-previous"
          >
            {AppStrings.PAM_REGULATOR_PORTAL_PREVIOUS}
          </Button>
          <Button
            intent="neutral"
            disabled={!canGoNext || resultsQuery.isFetching}
            onClick={() => setCursor(pagination?.nextCursor ?? undefined)}
            data-testid="regulator-portal-results-next"
          >
            {AppStrings.NEXT}
          </Button>
        </div>
      ) : null}
    </section>
  );
};

export default RegulatorPortalResults;
